import type { JSX } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Divider } from '@heroui/react';

import { RoleBadge } from '~/components/RoleBadge';

import type { OrgRole } from '~/types';

export interface InvitationCardProps {
  organizationName: string;
  inviterName?: string;
  inviterEmail?: string;
  role: OrgRole;
  expiresAt?: string;
  onAccept?: () => void;
  onDecline?: () => void;
  isAccepting?: boolean;
  isDeclining?: boolean;
  showActions?: boolean;
}

/**
 * Format expiration date using Intl.DateTimeFormat.
 */
function formatExpirationDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return dateString;
  }
}

export function InvitationCard(props: InvitationCardProps): JSX.Element {
  const {
    expiresAt,
    inviterEmail,
    inviterName,
    isAccepting = false,
    isDeclining = false,
    onAccept,
    onDecline,
    organizationName,
    role,
    showActions = true,
  } = props;
  const isLoading = isAccepting || isDeclining;

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-col items-start gap-2 pb-0">
        <h2 className="text-xl font-semibold">{organizationName}</h2>
        {inviterName && (
          <p className="text-sm text-default-500">
            Invited by {inviterName}
            {inviterEmail && ` (${inviterEmail})`}
          </p>
        )}
      </CardHeader>

      <CardBody className="gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-default-600">Role:</span>
          <RoleBadge role={role} />
        </div>

        {expiresAt && (
          <div className="text-sm text-default-500">
            <span className="text-default-600">Expires:</span> {formatExpirationDate(expiresAt)}
          </div>
        )}
      </CardBody>

      {showActions && (
        <>
          <Divider />
          <CardFooter className="gap-2">
            <Button
              color="primary"
              isLoading={isAccepting}
              isDisabled={isLoading}
              onPress={onAccept}
              className="flex-1"
            >
              Accept
            </Button>
            <Button
              color="default"
              isLoading={isDeclining}
              isDisabled={isLoading}
              onPress={onDecline}
              className="flex-1"
            >
              Decline
            </Button>
          </CardFooter>
        </>
      )}
    </Card>
  );
}
